
// accounts for unencoded & ampersands
const validateParamObj = function(obj, protocolDef, linkStr){
  var retObj = {};
  var invalidArr = [];

  Object.keys(obj).forEach((key) => {
    if(!protocolDef[key]){
      var invalidParamStr = key;
      if(obj[key]){
        invalidParamStr = `${key}=${obj[key]}`;
      }
      invalidArr.push(invalidParamStr);
    } else{
      retObj[key] = obj[key];
    }
  });
  invalidArr.forEach((paramStr) => {
    const invalidParamIdx = linkStr.indexOf(paramStr);
    Object.keys(retObj).forEach((key) => {
      const validParamStr = `${key}=${retObj[key]}`;
      const validParamIdx = linkStr.indexOf(validParamStr);
      if((validParamIdx + validParamStr.length + 1) === invalidParamIdx){
        retObj[key] = `${retObj[key]}&${paramStr}`
      }
    })
  })

  return retObj;
}

const coerceTypes = function(obj, protocolDef){
  var retObj = {};

  Object.keys(obj).forEach(function(key){
    const val = obj[key];
    if(val){
      if(protocolDef[key].type === Boolean){
        retObj[key] = val?.toLowerCase() === 'true';
      } else if(protocolDef[key].type === Number){
        retObj[key] = +val;
      } else{
        retObj[key] = val;
      }
    } else if(protocolDef[key].hasOwnProperty('default')){
      retObj[key] = protocolObj[key].default;
    }
  });

  return retObj;
}

/**
 * @class ActionLink
 * @classdesc An ActionLink is essentially a typical Uniform Resource Identifier (URI) for example, 'https://domain.com' or 'ftp://fileserver.com:3003' but with custom protocol schemes and parameters.  Each Action Event carries an Action Link with details about what type of action has occurred (eg. web link, app deep link, overlay open, etc) and their associated parameters like email recipients if for example the Action Link were to initiate an email draft
 * @memberof Flightkit
 * @param {string} linkStr Raw ActionLink protocol string with scheme, value, and parameters (eg. details://?contentId=123).  This string will be parsed and class instance will be hydrated.
 */
class ActionLink{

  /**
   * @member {Object} protocol Set of protocol scheme types
   * @memberof Flightkit.ActionLink
   * @static
   * @constant
   * @property {string} DETAILS (value=`details`) Opens a Content Instance details page as an overlay or can be injected into a FlybitsSlot for rendering.  If a contentId is not provided, it is assumed to attempt opening the details page of the Content Instance from which the action originated from.
   * @property {string} INAPPBROWSER (value=`web`) Opens a web URL in an in app browser.  This is dependent on application support.
   * @property {string} BROWSER (value=`browser`) Opens a web URL in the platform browser.
   * @property {string} HTTPS (value=`https`) Opens a web URL in the platform browser.  Allows for standard URLs as the raw link string; useful for RTF fields containing hyperlinks.
   * @property {string} CONCIERGE (value=`concierge`) Opens a particular Zone as an overlay or can be injected into a FlybitsSlot for rendering.
   * @property {string} PHONE (value=`phone`) Opens platform phone application with pre-seeded phone number.
   * @property {string} APP (value=`app`) Useful for mobile platform application deep linking.  Note URL must be registered in target application otherwise will default to be an simple web link opened in the platform browser.
   * @property {string} UNIVERSAL (value=`universalLink`) Meant for mobile platforms, it will open a platform specific URL and depending on the platform forward the action to specific applications with a failover as a webpage.
   * @property {string} MAIL (value=`mail`) Opens platform email application with pre-seeded body and details.
   */
  static protocol = {
    DETAILS: 'details',
    INAPPBROWSER: 'web',
    BROWSER: 'browser',
    HTTPS: 'https',
    PHONE: 'phone',
    APP: 'app',
    CONCIERGE: 'concierge',
    UNIVERSAL: 'universalLink',
    MAIL: 'mail',
    CUSTOM: 'custom'
  }

  /**
   * @typedef {Object} PROTOCOLDEFS Protocol scheme type definitions outlining what parameters corresponding ActionLink types contain.
   * @memberof Flightkit.ActionLink
   * @property {Object} details Type DETAILS
   * @property {string} details.contentId Flybits Content Instance ID
   * @property {boolean} [details.overlay=false] Flag to indicate Content details should be opened as an overlay.
   * @property {Object} web Type INAPPBROWSER
   * @property {string} web.url URL string to be opened in the browser.
   * @property {Object} browser Type BROWSER
   * @property {string} browser.url URL string to be opened in the browser.
   * @property {Object} https Type HTTPS; no additional properties.
   * @property {Object} phone Type PHONE
   * @property {string} phone.phoneNumber Phone number string that may contain dashes and plus symbols. 
   * @property {Object} app Type APP
   * @property {string} app.name Predefined reference string for custom application logic.
   * @property {Object} concierge Type CONCIERGE
   * @property {string} concierge.zoneReferenceId Zone Reference ID to load based on the ActionLink.
   * @property {boolean} [concierge.overlay=false] Flag to indicate Zone should be opened as an overlay.
   * @property {Object} universalLink Type UNIVERSAL
   * @property {string} universalLink.ios iOS specific universal URL. Note URL must be registered in target application otherwise will default to be an simple web link opened in the platform browser.
   * @property {string} universalLink.android Android specific universal URL.  Note URL must be registered in target application otherwise will default to be an simple web link opened in the platform browser.
   * @property {Object} mail Type MAIL
   * @property {string} mail.sendTo Recipient target email address.
   * @property {string} mail.subject Subject field of the email.
   * @property {string} mail.body Main email body.
   * @property {string} mail.cc Comma separated list of email addresses to be added to the Carbon Copy (CC) field.
   * @property {string} mail.bcc Comma separated list of email addresses to be added to the Bline Carbon Copy (BCC) field.
   */
  static PROTOCOLDEFS = {
    details: {
      contentId: {
        type: String,
      },
      overlay: {
        type: Boolean,
        default: false
      },
      title: {
        type: String
      }
    },
    web: {
      url: {
        type: String
      },
    },
    browser: {
      url: {
        type: String
      },
    },
    https: {},
    phone: {
      phoneNumber: {
        type: String // might contain dashes and plus symbols
      },
    },
    app: {
      name: {
        type: String
      },
    },
    concierge: {
      container: {
        type: String
      },
      zoneReferenceId: {
        type: String
      },
      filter: {
        type: String
      },
      style: {
        type: String
      },  
      overlay: {
        type: Boolean,
        default: false
      },  
    },
    universalLink: {
      ios: {
        type: String
      },
      android: {
        type: String
      },
    },
    mail: {
      sendTo: {
        type: String
      },
      subject: {
        type: String
      },
      body: {
        type: String
      },
      cc: {
        type: String
      },
      bcc: {
        type: String
      },
    },
    custom: {}
  };

  constructor(linkStr){
    /**
     * @instance
     * @memberof Flightkit.ActionLink
     * @member {string} rawStr Raw ActionLink protocol string with scheme, value, and parameters.
     */
    this.rawStr = '';
    /**
     * @instance
     * @memberof Flightkit.ActionLink
     * @member {string} protocol ActionLink protocol scheme (eg. details, browser, concierge, etc).
     */
    this.protocol = '';
    /**
     * @instance
     * @memberof Flightkit.ActionLink
     * @member {string} value ActionLink protocol value after scheme including parameters (eg flybits.com?x=3).
     */
    this.value = '';
    /**
     * @instance
     * @memberof Flightkit.ActionLink
     * @member {string} protoValue protocol value after scheme not including parameters (eg. flybits.com)
     */
    this.protoValue = '';
    /**
     * @instance
     * @memberof Flightkit.ActionLink
     * @member {Object} params Map of ActionLink protocol {@link Flightkit.ActionLink.PROTOCOLDEFS|type parameters} depending on the {@link Flightkit.ActionLink.protocol|type}.
     */
    this.params = {};
    /**
     * @instance
     * @memberof Flightkit.ActionLink
     * @member {Object} data ActionLink protocol {@link Flightkit.ActionLink.protocol|type} specific object that may contain entity instances (eg. hydrated Content Instances).
     */
    this.data = {};
    this.fromLink(linkStr);
  }

  fromLink(linkStr){
    this.rawStr = linkStr;
    var protocolStr = linkStr.split('://')[0] || '';
    protocolStr = protocolStr.toLowerCase();

    var valueStr = linkStr.indexOf('://') > -1 ? linkStr.substring(linkStr.indexOf('://') + 3) : linkStr;
    var protocolObj = ActionLink.PROTOCOLDEFS.custom;

    if(ActionLink.PROTOCOLDEFS[protocolStr]){
      protocolObj = ActionLink.PROTOCOLDEFS[protocolStr];
    } else{
      try{
        const parsedURL = new URL(linkStr);
        this.protoValue = linkStr.match(/:\/\/(.*?)\?/)[1];
        this.params = Object.fromEntries(parsedURL.searchParams.entries());
      } catch(e){
        console.warn('custom action is of unknown protocol scheme');
      }
    }

    this.protocol = protocolStr;
    this.value = valueStr;

    if(protocolObj && Object.keys(protocolObj).length){
      var paramObj = {};
      const parsedURL = new URL(linkStr);
      paramObj = Object.fromEntries(parsedURL.searchParams.entries());
      paramObj = validateParamObj(paramObj, protocolObj, linkStr);
      paramObj = coerceTypes(paramObj, protocolObj);

      this.params = paramObj;
    }
  }

  toJSON(){
    return {
      rawStr: this.rawStr,
      protocol: this.protocol,
      protoValue: this.protoValue,
      value: this.value,
      params: this.params,
      data: this.data
    };
  }

  static parse(linkStr){
    return new ActionLink(linkStr);
  }
}

export default ActionLink;